import {
  BirdEyeBoard,
  BirdEyeFinishedModal,
  BirdEyeKpiStrip,
  BirdEyePageEyebrow,
  BirdEyePageTitleBlock,
  CalendarShellHeader,
  CalendarTopBarActions,
  useBirdEyeBoard,
  useToast,
  type BirdEyeDragSource,
  type BirdEyeDropTarget,
} from '@elva/ui';
import { useAccountUser } from '@/app/layouts/useAccountUser';
import { extractApiErrorMessage } from '@/api/errors';
import { useLocationId } from '@/data/useLocationId';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  adaptBirdEyeCapturedSummary,
  adaptBirdEyeFinishedList,
  adaptBirdEyeKpiExtras,
  adaptBirdEyeResponse,
} from '../adapters/birdEyeAdapters';
import { resolveBirdEyeMove } from '../adapters/birdEyeMoves';
import { useBirdEye, useBirdEyeFinished, useUpdateBirdEyeStatus } from '../hooks/useBirdEye';
import { ElvaSectionState } from '@/components/ElvaSectionState';

export function BirdEyePage() {
  const accountUser = useAccountUser();
  const [finishedOpen, setFinishedOpen] = useState(false);
  const locationId = useLocationId() ?? null;

  const { data, isLoading, isError } = useBirdEye(locationId);
  const { data: finishedData } = useBirdEyeFinished(locationId);

  const preferredRoomRef = useRef(new Map<number, number>());

  const initialData = useMemo(
    () =>
      data
        ? adaptBirdEyeResponse(data, { preferredRoomByAppointment: preferredRoomRef.current })
        : undefined,
    [data],
  );

  // Re-sync whenever the payload actually changes (react-query keeps `data` referentially
  // stable when a refetch returns identical content). Keying on the etag alone missed
  // visit-state changes that didn't bump it, leaving the board on stale local state.
  const syncRevRef = useRef(0);
  const boardSyncKey = useMemo(() => {
    if (locationId == null || !data) return undefined;
    syncRevRef.current += 1;
    return `${locationId}:${syncRevRef.current}`;
  }, [locationId, data]);

  // FD82 endpoint totals, plus anything finished on the live board that the endpoint
  // hasn't caught up with yet; live-board only until the endpoint responds.
  const finishedList = useMemo(
    () => adaptBirdEyeFinishedList(finishedData),
    [finishedData],
  );
  const finishedSummary = useMemo(
    () => adaptBirdEyeCapturedSummary(finishedData, data),
    [finishedData, data],
  );

  const kpiExtras = useMemo(() => adaptBirdEyeKpiExtras(data), [data]);

  const { state, movePatient, resetBoard, counts } = useBirdEyeBoard(initialData, boardSyncKey);

  const updateStatus = useUpdateBirdEyeStatus(locationId);
  const { toast } = useToast();

  const handleMove = useCallback(
    (from: BirdEyeDragSource, to: BirdEyeDropTarget) => {
      const move = resolveBirdEyeMove(state, from, to);
      if (move.kind === 'noop') return;
      if (move.kind === 'unsupported') {
        toast({ title: 'Can’t move patient', description: move.reason, variant: 'error' });
        return;
      }
      if (to.type === 'operatory') preferredRoomRef.current.set(move.appointmentId, to.index);
      else preferredRoomRef.current.delete(move.appointmentId);
      movePatient(from, to);
      updateStatus.mutate(
        { appointmentId: move.appointmentId, status: move.status },
        {
          onError: (err: unknown) => {
            preferredRoomRef.current.delete(move.appointmentId);
            resetBoard();
            toast({
              title: 'Couldn’t update status',
              description: extractApiErrorMessage(err),
              variant: 'error',
            });
          },
        },
      );
    },
    [state, movePatient, resetBoard, updateStatus, toast],
  );

  return (
    <>
      <div className="be-glow pointer-events-none fixed" aria-hidden />
      <CalendarShellHeader
        eyebrow={<BirdEyePageEyebrow />}
        left={<BirdEyePageTitleBlock />}
        actions={<CalendarTopBarActions showOptions={false} />}
        user={accountUser}
      />
      <div className="relative z-[1] flex min-h-0 flex-1 flex-col overflow-hidden px-[30px]">
        <BirdEyeKpiStrip
          counts={counts}
          {...(finishedSummary ? { finishedSummary } : {})}
          {...(kpiExtras ? { extras: kpiExtras } : {})}
        />
        <ElvaSectionState isLoading={isLoading && !data} isError={isError && !data}>
          <BirdEyeBoard
            state={state}
            onMove={handleMove}
            {...(finishedSummary ? { finishedSummary } : {})}
            onViewFinished={() => {
              setFinishedOpen(true);
            }}
          />
        </ElvaSectionState>
      </div>
      <BirdEyeFinishedModal
        open={finishedOpen}
        onClose={() => {
          setFinishedOpen(false);
        }}
        {...(finishedSummary ? { finishedSummary } : {})}
        finishedList={finishedList}
      />
    </>
  );
}
